import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel } from '@vizit/atoms';
import { FormDialog, FormSelect, FormTextField } from '@vizit/forms';
import { PlusIcon } from '@vizit/icons';
import { SelectItem } from '@vizit/molecules';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { FormProductCategoryComboBox, type FormProductCategoryComboBoxProps } from '../form-product-category-combo-box';

type CollectionsFormSchema = {
  name: string;
  category: string;
  imageType: 'hero' | 'carousel';
};

interface CollectionsDialogProps {
  /**
   * Error message from submission attempt
   *
   * @optional
   * @type {string | null}
   */
  submissionErrorMessage?: string | null;
  /**
   * Query props for the product category combo box
   */
  productCategoryComboBoxProps: Omit<
    FormProductCategoryComboBoxProps<CollectionsFormSchema>,
    'control' | 'name' | 'label' | 'isRequired'
  >;
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * The text for the trigger button
   *
   * @optional
   * @type {string}
   */
  triggerButtonText?: string;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * Callback function to handle the collection creation
   */
  onSubmit: (data: CollectionsFormSchema) => Promise<void>;
  /**
   * Callback function when dialog open state changes
   *
   * @optional
   * @param isOpen - Whether the dialog is open
   */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Dialog component for creating a new collection
 * Allows users to enter collection details including name, product category, and image type
 *
 * @param props - The component props
 * @param props.onSubmit - Callback function when form is submitted
 *
 * @example
 * <CollectionsDialog onSubmit={(data) => console.log(data)} />
 */
function CollectionsDialog(props: CollectionsDialogProps) {
  const {
    submissionErrorMessage = null,
    productCategoryComboBoxProps,
    isOpen,
    hideTrigger = false,
    triggerButtonText = 'Create collection',
    useIcon = false,
    onSubmit,
    onOpenChange,
  } = props;

  const formSchema = yup.object().shape({
    name: yup.string().required('Collection name is required'),
    category: yup
      .string()
      .oneOf(
        productCategoryComboBoxProps.items.map((item) => item.id),
        'Product category is required',
      )
      .required('Product category is required'),
    imageType: yup
      .string()
      .oneOf(['hero', 'carousel'], 'Image type must be either Hero or Carousel')
      .required('Image type is required'),
  });

  const formMethods = useForm<CollectionsFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      name: '',
      category: '',
      imageType: undefined,
    },
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, reset, setError, clearErrors } = formMethods;

  const handleOnResetForm = () => {
    clearErrors();

    reset({
      name: '',
      category: '',
      imageType: undefined,
    });
  };

  const handleFormSubmit = async (data: CollectionsFormSchema) => {
    await onSubmit(data);
  };

  React.useEffect(() => {
    if (submissionErrorMessage) {
      setError('name', { message: submissionErrorMessage });
    }
  }, [submissionErrorMessage, setError]);

  return (
    <FormDialog
      formMethods={formMethods}
      formName="collections"
      isOpen={isOpen}
      onSubmit={handleSubmit(handleFormSubmit)}
      onResetForm={handleOnResetForm}
      onOpenChange={onOpenChange}
      data-testid="collection-dialog"
    >
      <FormDialog.Trigger>
        {hideTrigger ? (
          <Button className="sr-only" aria-hidden="true" isDisabled>
            Open dialog
          </Button>
        ) : (
          <Button
            level={ButtonLevel.Primary}
            aria-label={triggerButtonText}
            iconStart={useIcon ? undefined : <PlusIcon size={20} />}
            data-testid="collection-trigger"
          >
            {useIcon ? <PlusIcon size={20} /> : triggerButtonText}
          </Button>
        )}
      </FormDialog.Trigger>
      <FormDialog.Title>Create a collection</FormDialog.Title>
      <FormDialog.Subtitle>
        Name your collection and choose product category to benchmark your assets.
      </FormDialog.Subtitle>
      <FormDialog.FormFields>
        <FormTextField
          control={control}
          name="name"
          label="Collection name"
          placeholder="Enter collection name"
          isRequired
          inputProps={{ 'data-testid': 'collection-name-input' }}
        />
        <FormProductCategoryComboBox
          control={control}
          name="category"
          label="Product category"
          isRequired
          menuTrigger="input"
          data-testid="collection-category"
          {...productCategoryComboBoxProps}
        />
        <FormSelect
          control={control}
          name="imageType"
          label="Image type"
          placeholder="Select image type"
          isRequired
          data-testid="collection-image-type"
        >
          <SelectItem id="hero" textValue="Hero">
            Hero
          </SelectItem>
          <SelectItem id="carousel" textValue="Carousel">
            Carousel
          </SelectItem>
        </FormSelect>
      </FormDialog.FormFields>
      <FormDialog.SubmitButton data-testid="collection-submit">Create collection</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { CollectionsDialog };
export type { CollectionsFormSchema };
